import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  OS_DEFAULT_SELECTION,
  listedDevices,
  planDeviceChange,
  resolveDeviceId,
  type CallDevice,
  type CallDeviceKind,
  type CallDevicePhase,
  type DeviceNotice,
  type DeviceSelection,
} from "../lib/callDevices";

/** A headset can arrive as two events (input, then output) — wait for both. */
export const DEVICE_CHANGE_DEBOUNCE_MS = 300;
export const DEVICE_NOTICE_MS = 6000;

export type SwitchDevice = (
  kind: CallDeviceKind,
  deviceId: string,
) => Promise<unknown> | void;

export interface UseCallDevicesOptions {
  /** Pre-join screens only list new devices; in a call they auto-switch. */
  phase: CallDevicePhase;
  /** Moves the live track (in a call: `room.switchActiveDevice`). */
  switchDevice: SwitchDevice;
  /** Carry-over from the lobby; defaults to the OS default for every kind. */
  initialSelection?: Partial<DeviceSelection>;
}

export interface CallDevicesNotice {
  notice: DeviceNotice;
  canUndo: boolean;
}

export interface UseCallDevicesResult {
  microphones: CallDevice[];
  speakers: CallDevice[];
  cameras: CallDevice[];
  /** `null` = the OS default. */
  selection: DeviceSelection;
  /** The id a menu marks selected — the OS default resolved to a real id. */
  selectedId: (kind: CallDeviceKind) => string | null;
  /** A manual pick from the menu. */
  select: (kind: CallDeviceKind, deviceId: string) => void;
  notice: CallDevicesNotice | null;
  undo: () => void;
  dismissNotice: () => void;
  /** Re-reads the device list now (a menu opening — a permission grant
   *  doesn't always fire `devicechange`). */
  refresh: () => void;
}

function toCallDevices(infos: MediaDeviceInfo[]): CallDevice[] {
  return infos
    .filter(
      (d): d is MediaDeviceInfo & { kind: CallDeviceKind } =>
        d.kind === "audioinput" ||
        d.kind === "audiooutput" ||
        d.kind === "videoinput",
    )
    .map((d) => ({
      deviceId: d.deviceId,
      kind: d.kind,
      label: d.label,
      groupId: d.groupId,
    }));
}

/**
 * Device lists, selection, headset auto-switch and disconnect fallback for one
 * call surface (GH-1859). The rules live in `lib/callDevices.ts`; this hook
 * owns the `devicechange` listener, the notice timer, Undo and the switching.
 *
 * LiveKit 2.19 moves a lost speaker by itself on Chrome but leaves a lost mic
 * (and camera) alone, so the fallback here is load-bearing, not a duplicate.
 */
export function useCallDevices({
  phase,
  switchDevice,
  initialSelection,
}: UseCallDevicesOptions): UseCallDevicesResult {
  const [devices, setDevices] = useState<CallDevice[]>([]);
  const [selection, setSelection] = useState<DeviceSelection>(() => ({
    ...OS_DEFAULT_SELECTION,
    ...initialSelection,
  }));
  const [notice, setNotice] = useState<CallDevicesNotice | null>(null);

  // Read inside async/listener code, so mirrored into refs.
  const knownRef = useRef<CallDevice[] | null>(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const switchRef = useRef(switchDevice);
  switchRef.current = switchDevice;
  const undoRef = useRef<DeviceSelection | null>(null);
  const noticeTimerRef = useRef<number | undefined>(undefined);
  const refreshRef = useRef<() => void>(() => {});

  const runSwitch = useCallback((kind: CallDeviceKind, deviceId: string) => {
    try {
      void Promise.resolve(switchRef.current(kind, deviceId)).catch((err) =>
        console.error("switchDevice failed:", err),
      );
    } catch (err) {
      console.error("switchDevice failed:", err);
    }
  }, []);

  const dismissNotice = useCallback(() => {
    window.clearTimeout(noticeTimerRef.current);
    undoRef.current = null;
    setNotice(null);
  }, []);

  const showNotice = useCallback(
    (next: DeviceNotice, undo: DeviceSelection | null) => {
      window.clearTimeout(noticeTimerRef.current);
      undoRef.current = undo;
      setNotice({ notice: next, canUndo: undo !== null });
      noticeTimerRef.current = window.setTimeout(dismissNotice, DEVICE_NOTICE_MS);
    },
    [dismissNotice],
  );

  useEffect(() => {
    const media = navigator.mediaDevices;
    if (!media?.enumerateDevices) return;
    let cancelled = false;
    let latest = 0;
    let debounce: number | undefined;

    const refresh = async () => {
      const seq = ++latest;
      let next: CallDevice[];
      try {
        next = toCallDevices(await media.enumerateDevices());
      } catch {
        return;
      }
      if (cancelled || seq !== latest) return;

      const plan = planDeviceChange(
        knownRef.current,
        next,
        selectionRef.current,
        phaseRef.current,
      );
      knownRef.current = next;
      setDevices(next);
      if (plan.selection !== selectionRef.current) {
        selectionRef.current = plan.selection;
        setSelection(plan.selection);
      }
      for (const s of plan.switches) runSwitch(s.kind, s.deviceId);
      if (plan.notice) showNotice(plan.notice, plan.undo);
    };

    const onChange = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void refresh(), DEVICE_CHANGE_DEBOUNCE_MS);
    };

    refreshRef.current = () => void refresh();
    void refresh();
    media.addEventListener("devicechange", onChange);
    return () => {
      cancelled = true;
      refreshRef.current = () => {};
      window.clearTimeout(debounce);
      media.removeEventListener("devicechange", onChange);
    };
  }, [runSwitch, showNotice]);

  useEffect(() => () => window.clearTimeout(noticeTimerRef.current), []);

  const select = useCallback(
    (kind: CallDeviceKind, deviceId: string) => {
      const next = { ...selectionRef.current, [kind]: deviceId };
      selectionRef.current = next;
      setSelection(next);
      runSwitch(kind, deviceId);
      // A manual pick supersedes whatever the notice offered to undo.
      if (undoRef.current) dismissNotice();
    },
    [runSwitch, dismissNotice],
  );

  const undo = useCallback(() => {
    const previous = undoRef.current;
    if (!previous) return;
    const known = knownRef.current ?? [];
    const next = { ...selectionRef.current };
    for (const kind of ["audioinput", "audiooutput"] as const) {
      // A device that left since the switch can't come back — use the default.
      const id = resolveDeviceId(known, kind, previous[kind]);
      next[kind] = id === previous[kind] ? previous[kind] : null;
      if (id !== null) runSwitch(kind, id);
    }
    selectionRef.current = next;
    setSelection(next);
    dismissNotice();
  }, [runSwitch, dismissNotice]);

  const microphones = useMemo(() => listedDevices(devices, "audioinput"), [devices]);
  const speakers = useMemo(() => listedDevices(devices, "audiooutput"), [devices]);
  const cameras = useMemo(() => listedDevices(devices, "videoinput"), [devices]);

  const selectedId = useCallback(
    (kind: CallDeviceKind) => {
      const id = resolveDeviceId(devices, kind, selection[kind]);
      if (id !== "default") return id;
      // Mark the real device the "Default" entry mirrors (same groupId).
      const pseudo = devices.find((d) => d.kind === kind && d.deviceId === "default");
      const twin = listedDevices(devices, kind).find(
        (d) => pseudo?.groupId && d.groupId === pseudo.groupId,
      );
      return twin?.deviceId ?? listedDevices(devices, kind)[0]?.deviceId ?? null;
    },
    [devices, selection],
  );

  const refresh = useCallback(() => refreshRef.current(), []);

  return {
    microphones,
    speakers,
    cameras,
    selection,
    selectedId,
    select,
    notice,
    undo,
    dismissNotice,
    refresh,
  };
}

const noSwitch: SwitchDevice = () => {};

/**
 * A pre-join screen's devices: lists and picks only — nothing is live yet, so
 * there's no track to switch, no auto-switch and no notice. The picks are
 * carried into the call through `PreJoinChoices`.
 */
export function usePreJoinDevices(
  initialSelection?: Partial<DeviceSelection>,
): UseCallDevicesResult {
  return useCallDevices({ phase: "prejoin", switchDevice: noSwitch, initialSelection });
}
